import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Hero from '../../components/Hero';
import CallToAction from '../../components/CallToAction';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLaptopCode, faMicrochip, faDatabase, faMobileAlt, faGlobe, faChartLine, faRobot, faShieldAlt, faCheckCircle, faGamepad, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import '../SoftwareServices.css';

import projectsHeroImage from '../../assets/images/projects.jpg';

const SoftwareServicesEN = () => {
  const services = [
    { icon: faLaptopCode, title: 'Enterprise Software Solutions', description: 'We develop custom ERP, CRM and business process management systems. Scalable and secure corporate software that integrates with your existing systems.', features: ['ERP & CRM Systems', 'Business Process Automation', 'Enterprise Portal Solutions'] },
    { icon: faMobileAlt, title: 'Mobile App Development', description: 'Native and cross-platform mobile applications for iOS and Android. High-performance apps with a focus on user experience.', features: ['iOS & Android Apps', 'Cross-Platform (React Native)', 'Push Notifications & Native Features'] },
    { icon: faGlobe, title: 'Web Application Development', description: 'Powerful, fast and responsive web applications with modern technologies. SPA, PWA and e-commerce solutions to strengthen your digital presence.', features: ['SPA & PWA Applications', 'E-Commerce Solutions', 'SEO-Friendly & Responsive'] },
    { icon: faRobot, title: 'AI & Machine Learning', description: 'AI models and machine learning solutions that create value from your data. NLP, image recognition and forecasting systems.', features: ['NLP & Chatbot Systems', 'Image Processing', 'Forecasting Models'] },
    { icon: faMicrochip, title: 'IoT & Embedded Systems', description: 'IoT solutions to make your devices smart. Sensor integrations, SCADA systems and industrial automation.', features: ['Sensor Integration', 'SCADA & Automation', 'Edge Computing'] },
    { icon: faChartLine, title: 'Smart Reporting & Analytics', description: 'Business intelligence and data visualization solutions. Real-time dashboards and customized analytics systems.', features: ['Business Intelligence', 'Dashboard Development', 'Data Visualization'] },
    { icon: faDatabase, title: 'Education Technology Software', description: 'Custom software for educational institutions and publishers. LMS platforms, interactive content and smart board applications.', features: ['LMS Platforms', 'Interactive Content', 'Smart Board Applications'] },
    { icon: faShieldAlt, title: 'Cloud & DevOps Solutions', description: 'Deploy and manage applications on AWS, Azure and Google Cloud. CI/CD pipelines and auto-scaling solutions.', features: ['Cloud Migration', 'CI/CD Pipeline', 'Monitoring & Logging'] },
    { icon: faGamepad, title: 'Game Development', description: '2D and 3D games, casual and strategy titles. Game development for PC, console and mobile platforms.', features: ['2D & 3D Game Projects', 'Mobile Game Development', 'Gamification & Reward Systems'] },
  ];

  const sectorSolutions = [
    { title: 'Education Software', description: 'LMS, video lessons, certification and exam modules', features: ['Learning management systems', 'Video streaming & live lessons', 'Progress tracking & reporting'] },
    { title: 'E-Commerce Software', description: 'Online store, payment, inventory and order management', features: ['Product catalog & cart', 'Payment system integration', 'Shipping & invoice integration'] },
    { title: 'Finance & Fintech', description: 'Payment, transfer, reporting and compliance solutions', features: ['Payment & transfer modules', 'Secure authentication', 'Reporting & compliance'] },
    { title: 'Healthcare Software', description: 'Appointments, patient tracking and medical records', features: ['Appointment & patient management', 'Compliant data processing', 'Digital records & archive'] },
    { title: 'CRM Solutions', description: 'Customer database, sales funnel and communication tracking', features: ['Customer & lead management', 'Sales pipeline', 'Email & communication log'] },
    { title: 'Logistics Software', description: 'Fleet tracking, route optimization and warehouse management', features: ['Fleet & driver tracking', 'Route & delivery planning', 'Warehouse & stock management'] },
    { title: 'Human Resources', description: 'Recruitment, performance, leave and payroll processes', features: ['HR processes & forms', 'Performance & goal tracking', 'Corporate portal'] },
    { title: 'Content Management (CMS)', description: 'Multilingual, multi-channel content management systems', features: ['Dynamic page management', 'Multi-language & media', 'SEO & publishing workflow'] },
    { title: 'Project Management', description: 'Tasks, sprints, time tracking and resource planning', features: ['Task & sprint management', 'Gantt & kanban views', 'Team & role management'] },
    { title: 'Social Media & Community', description: 'Social networks, forums, content sharing and notifications', features: ['Profile & engagement', 'Content sharing & comments', 'Moderation & security'] },
    { title: 'Restaurant & Food', description: 'Online ordering, table management and QR menu', features: ['Online order & reservation', 'QR menu & payment', 'Kitchen & stock integration'] },
    { title: 'Dashboard & Analytics', description: 'KPI tracking, reporting and data visualization', features: ['Customizable KPI panels', 'Real-time reporting', 'External system integration'] },
  ];

  const faqItems = [
    { q: 'How do I get a quote for a software project?', a: 'Use our price calculator to select project type and features for an instant estimated budget. Contact us via the form for a detailed quote.' },
    { q: 'Is the quote free?', a: 'Yes. All price quotes and consultancy are completely free with no payment commitment.' },
    { q: 'What types of software projects do you quote for?', a: 'We provide quotes for web applications, mobile apps (iOS/Android), game development (PC, mobile, 2D/3D) and custom software projects.' },
  ];

  return (
    <div className="software-services">
      <Helmet>
        <title>ProgenX Software Services | Enterprise Software, AI, IoT Solutions 2026</title>
        <meta name="description" content="ProgenX software services: Enterprise software, mobile app development, web applications, AI, IoT, smart reporting and education technology. Ankara-based software company." />
        <meta name="keywords" content="software services, enterprise software, mobile app, web development, AI, IoT, smart reporting, education software, game development, e-commerce" />
        <meta property="og:title" content="ProgenX Software Services | Enterprise Software and AI Solutions" />
        <meta property="og:description" content="Professional software services in enterprise software, AI, IoT and smart reporting." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://progenx.com.tr/en/software-services" />
        <link rel="canonical" href="https://progenx.com.tr/en/software-services" />
        <link rel="alternate" hrefLang="tr" href="https://progenx.com.tr/yazilim-hizmetleri" />
        <link rel="alternate" hrefLang="en" href="https://progenx.com.tr/en/software-services" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqItems.map(item => ({
            "@type": "Question",
            "name": item.q,
            "acceptedAnswer": { "@type": "Answer", "text": item.a }
          }))
        })}</script>
      </Helmet>

      <Hero title="SOFTWARE SERVICES" backgroundImage={projectsHeroImage} />

      <section className="services-content">
        <div className="container">
          <h2 className="section-title">Our Software Solutions</h2>
          <p className="services-intro">
            At ProgenX, we offer software services across a wide spectrum from education to industry. 
            We deliver custom, scalable and sustainable solutions for every project, serving as a 
            trusted technology partner in our clients' digital transformation journeys.
          </p>
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-card">
                <div className="service-icon"><FontAwesomeIcon icon={service.icon} /></div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
                <ul className="service-features">
                  {service.features.map((feature, i) => (
                    <li key={i}><FontAwesomeIcon icon={faCheckCircle} className="feature-check-icon" />{feature}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="sector-solutions">
        <div className="container">
          <h2 className="section-title">Industry-Specific Software Solutions</h2>
          <p className="services-intro">
            Education, IoT, e-commerce, finance, healthcare, CRM, logistics and more. 
            We digitize your business processes with industry-specific software solutions.
          </p>
          <div className="sector-grid">
            {sectorSolutions.map((solution, index) => (
              <div key={index} className="sector-card">
                <h3 className="sector-card-title">{solution.title}</h3>
                <p className="sector-card-desc">{solution.description}</p>
                <ul className="sector-card-features">
                  {solution.features.map((feature, i) => (
                    <li key={i}><FontAwesomeIcon icon={faCheckCircle} className="feature-check-icon" />{feature}</li>
                  ))}
                </ul>
                <Link to="/en/software-calculator" className="sector-card-link">
                  Get Quote <FontAwesomeIcon icon={faArrowRight} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="services-faq">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-list">
            {faqItems.map((item, index) => (
              <div key={index} className="faq-item">
                <h3 className="faq-question">{item.q}</h3>
                <p className="faq-answer">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="services-cta-section">
        <div className="container">
          <h3>Get a Cost Estimate for Your Project</h3>
          <p>Use our free tool to calculate the estimated cost of your software projects.</p>
          <div className="services-cta-buttons">
            <Link to="/en/software-calculator" className="btn btn-primary">CALCULATE COST <FontAwesomeIcon icon={faArrowRight} /></Link>
            <Link to="/en/contact" className="btn">CONTACT US</Link>
          </div>
        </div>
      </section>

      <CallToAction 
        title="Let's Bring Your Project to Life Together"
        subtitle="Contact us for your software needs, let's create the right solution together."
        buttonText="Contact Us" 
        buttonLink="/en/contact" 
      />
    </div>
  );
};

export default SoftwareServicesEN;
